import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError, uploadFile, fileUrl, formatTanggal } from "@/lib/api";
import Layout, { PageTitle } from "@/components/Layout";
import { NAV_PUSTAKA } from "@/lib/nav";
import { toast } from "sonner";
import {
  Plus, Trash2, Upload, Check, XCircle, Undo2, Download, BookOpen, ArrowLeft,
} from "lucide-react";
import { BukuDigitalReader } from "@/pages/pustaka/BukuDigitalShared";

const KELAS_TEKS = ["PAUD", "TKLB", "Kelas I", "Kelas II", "Kelas III", "Kelas IV", "Kelas V", "Kelas VI", "Kelas VII", "Kelas VIII", "Kelas IX", "Kelas X", "Kelas XI", "Kelas XII"];
const KELAS_FISIK = ["Kelas I", "Kelas II", "Kelas III", "Kelas IV", "Kelas V", "Kelas VI", "Kelas VII", "Kelas VIII", "Kelas IX", "Kelas X", "Kelas XI", "Kelas XII"];
const JENIS_UMUM = ["Fiksi", "Non-Fiksi", "Referensi", "Komik", "Majalah", "Ensiklopedia"];

/* ═══════════════════════ EXCEL BULK CONTROLS (fisik) ═══════════════════════ */
function ExcelBukuControls() {
  const [busy, setBusy] = useState(false);
  const downloadTemplate = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/excel/buku/template`;
    window.open(url, "_blank");
  };
  const handleUpload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/excel/buku/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Berhasil import: ${data.inserted}`);
      if (data.errors?.length) toast.error(`Error: ${data.errors.join(" | ")}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div className="flex gap-2">
      <button onClick={downloadTemplate} className="pp-btn-secondary" data-testid="dl-template-buku">
        <Download className="w-4 h-4" /> Template Excel
      </button>
      <label className="pp-btn-secondary cursor-pointer" data-testid="upload-buku-label">
        <Upload className="w-4 h-4" /> {busy ? "Mengimport…" : "Upload Excel"}
        <input type="file" accept=".xlsx" onChange={handleUpload} className="hidden" />
      </label>
    </div>
  );
}

/* ═══════════════════════ INPUT BUKU FISIK ═══════════════════════ */
const emptyFisik = { judul: "", penulis: "", kategori: "buku_teks_pelajaran", jenis_buku: KELAS_FISIK[0], jumlah: 1, cover_file_id: null, format: "fisik" };

export function InputBukuFisik() {
  const [form, setForm] = useState(emptyFisik);
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();

  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try { const r = await uploadFile(f); setForm({ ...form, cover_file_id: r.id }); toast.success("Cover diunggah"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/buku", { ...form, jumlah: parseInt(form.jumlah) });
      toast.success("Buku fisik tersimpan");
      navigate("/pustaka/buku/fisik");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const jenisList = form.kategori === "buku_teks_pelajaran" ? KELAS_FISIK : JENIS_UMUM;

  return (
    <Layout nav={NAV_PUSTAKA}>
      <PageTitle title="Input Buku Fisik" action={
        <button onClick={() => navigate("/pustaka/buku/fisik")} className="pp-btn-secondary">
          <ArrowLeft className="w-4 h-4" /> Kembali
        </button>
      } />
      <section className="pp-card p-5 mb-5 max-w-2xl">
        <h3 className="font-semibold mb-3 text-slate-900">Bulk Upload via Excel</h3>
        <ExcelBukuControls />
      </section>
      <section className="pp-card p-6 sm:p-8 max-w-2xl">
        <form onSubmit={submit} className="space-y-4" data-testid="buku-fisik-form">
          <div><label className="pp-label">Judul Buku *</label><input required className="pp-input" value={form.judul} onChange={(e) => setForm({ ...form, judul: e.target.value })} data-testid="buku-judul" /></div>
          <div><label className="pp-label">Penulis *</label><input required className="pp-input" value={form.penulis} onChange={(e) => setForm({ ...form, penulis: e.target.value })} /></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className="pp-label">Kategori</label>
              <select className="pp-input" value={form.kategori} onChange={(e) => setForm({ ...form, kategori: e.target.value, jenis_buku: e.target.value === "buku_teks_pelajaran" ? KELAS_FISIK[0] : JENIS_UMUM[0] })}>
                <option value="buku_teks_pelajaran">Buku Teks Pelajaran</option>
                <option value="buku_umum">Buku Umum</option>
              </select></div>
            <div><label className="pp-label">{form.kategori === "buku_teks_pelajaran" ? "Kelas" : "Jenis Buku"}</label>
              <select className="pp-input" value={form.jenis_buku} onChange={(e) => setForm({ ...form, jenis_buku: e.target.value })}>
                {jenisList.map(j => <option key={j}>{j}</option>)}
              </select></div>
          </div>
          <div><label className="pp-label">Jumlah Eksemplar *</label><input required type="number" min={1} className="pp-input" value={form.jumlah} onChange={(e) => setForm({ ...form, jumlah: e.target.value })} /></div>
          <div><label className="pp-label">Cover Buku</label>
            <label className="pp-btn-secondary cursor-pointer">
              <Upload className="w-4 h-4" />{uploading ? "Mengunggah…" : (form.cover_file_id ? "Ganti Cover" : "Pilih Foto Cover")}
              <input type="file" accept="image/*" onChange={upload} className="hidden" />
            </label>
            {form.cover_file_id && <span className="ml-3 text-sm text-emerald-600">✓ Cover siap</span>}
          </div>
          <div className="flex justify-end pt-2 border-t border-slate-100">
            <button type="submit" className="pp-btn-primary" data-testid="submit-buku-fisik">Simpan Buku</button>
          </div>
        </form>
      </section>
    </Layout>
  );
}

/* ═══════════════════════ DAFTAR BUKU FISIK ═══════════════════════ */
export function DaftarBukuFisik() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState(""); const [penulis, setPenulis] = useState(""); const [kat, setKat] = useState("");
  const [applied, setApplied] = useState({ q: "", penulis: "", kat: "" });
  const navigate = useNavigate();

  const load = useCallback(async () => {
    const params = { format: "fisik" };
    if (applied.q) params.q = applied.q; if (applied.penulis) params.penulis = applied.penulis; if (applied.kat) params.kategori = applied.kat;
    const { data } = await api.get("/buku", { params }); setItems(data);
  }, [applied]);
  useEffect(() => { load(); }, [load]);
  const apply = () => setApplied({ q, penulis, kat });
  const reset = () => { setQ(""); setPenulis(""); setKat(""); setApplied({ q: "", penulis: "", kat: "" }); };
  const del = async (id) => { if (!window.confirm("Hapus buku?")) return; await api.delete(`/buku/${id}`); toast.success("Dihapus"); load(); };

  return (
    <Layout nav={NAV_PUSTAKA}>
      <PageTitle title="Daftar Buku Fisik" action={
        <button onClick={() => navigate("/pustaka/buku/fisik/input")} className="pp-btn-primary" data-testid="btn-input-buku-fisik">
          <Plus className="w-4 h-4" /> Input Buku Fisik
        </button>
      } />
      <section className="pp-card p-5 mb-6 grid sm:grid-cols-4 gap-3">
        <input className="pp-input" placeholder="Cari judul" value={q} onChange={(e) => setQ(e.target.value)} data-testid="filter-judul-fisik" />
        <input className="pp-input" placeholder="Penulis" value={penulis} onChange={(e) => setPenulis(e.target.value)} data-testid="filter-penulis-fisik" />
        <select className="pp-input" value={kat} onChange={(e) => setKat(e.target.value)} data-testid="filter-kategori-fisik">
          <option value="">Semua kategori</option>
          <option value="buku_teks_pelajaran">Buku Teks</option>
          <option value="buku_umum">Buku Umum</option>
        </select>
        <div className="flex gap-2">
          <button onClick={load} className="pp-btn-primary flex-1">Terapkan</button>
          <button onClick={() => { setQ(""); setPenulis(""); setKat(""); setTimeout(load, 0); }} className="pp-btn-secondary flex-1">Reset</button>
        </div>
      </section>
      <section className="pp-card pp-freeze overflow-hidden">
        <table className="w-full text-sm" data-testid="buku-fisik-table">
          <thead><tr className="pp-table-header">
            <th className="text-left px-5 py-3">Cover</th>
            <th className="text-left px-5 py-3">Judul</th>
            <th className="text-left px-5 py-3">Penulis</th>
            <th className="text-left px-5 py-3">Kategori</th>
            <th className="text-left px-5 py-3">Jenis</th>
            <th className="text-center px-5 py-3">Stok</th>
            <th className="text-right px-5 py-3">Aksi</th>
          </tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-slate-400 italic">Belum ada buku fisik.</td></tr>}
            {items.map(b => (
              <tr key={b.id} className="pp-row">
                <td className="px-5 py-3">{b.cover_file_id ? <img src={fileUrl(b.cover_file_id)} className="w-10 h-14 object-cover rounded" alt="" /> : <div className="w-10 h-14 bg-slate-100 rounded" />}</td>
                <td className="px-5 py-3 font-medium">{b.judul}</td>
                <td className="px-5 py-3 text-slate-600">{b.penulis}</td>
                <td className="px-5 py-3 text-xs">{b.kategori.replace("_", " ")}</td>
                <td className="px-5 py-3 text-xs">{b.jenis_buku}</td>
                <td className="px-5 py-3 text-center font-mono">{b.ready}/{b.jumlah}</td>
                <td className="px-5 py-3 text-right"><button onClick={() => del(b.id)} className="pp-btn-danger"><Trash2 className="w-3.5 h-3.5" />Hapus</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </Layout>
  );
}

/* Excel bulk controls (buku digital) */
function ExcelBukuDigitalControls({ onReload }) {
  const [busy, setBusy] = useState(false);
  const downloadTemplate = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/excel/buku-digital/template`;
    window.open(url, "_blank");
  };
  const handleUpload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/excel/buku-digital/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Berhasil import: ${data.inserted}`);
      if (data.errors?.length) toast.error(`Error: ${data.errors.join(" | ")}`);
      if (onReload) onReload();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <>
      <button onClick={downloadTemplate} className="pp-btn-secondary" data-testid="dl-template-buku-digital">
        <Download className="w-4 h-4" /> Template Excel
      </button>
      <label className="pp-btn-secondary cursor-pointer" data-testid="upload-buku-digital-label">
        <Upload className="w-4 h-4" /> {busy ? "Mengimport…" : "Upload Excel"}
        <input type="file" accept=".xlsx" onChange={handleUpload} className="hidden" data-testid="upload-buku-digital-input" />
      </label>
    </>
  );
}
/* ═══════════════════════ INPUT BUKU DIGITAL ═══════════════════════ */
const emptyDigital = { judul: "", penulis: "", kategori: "buku_teks_pelajaran", jenis_buku: KELAS_TEKS[0], cover_file_id: null, pdf_file_id: null, drive_url: "", format: "digital", jumlah: 1 };

export function InputBukuDigital() {
  const [form, setForm] = useState(emptyDigital);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const jenisList = form.kategori === "buku_teks_pelajaran" ? KELAS_TEKS : JENIS_UMUM;

  const uploadCover = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploadingCover(true);
    try { const r = await uploadFile(f); setForm((s) => ({ ...s, cover_file_id: r.id })); toast.success("Cover diunggah"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploadingCover(false); }
  };


  const submit = async (e) => {
    e.preventDefault();
    const link = (form.drive_url || "").trim();
    if (!link) { toast.error("Wajib mengisi link Google Drive PDF"); return; }
    const low = link.toLowerCase();
    const okUrl = (low.startsWith("http://") || low.startsWith("https://")) && (low.includes("drive.google.com") || low.includes("docs.google.com"));
    if (!okUrl) { toast.error("Link harus URL Google Drive/Docs"); return; }
    setSaving(true);
    try {
      await api.post("/buku", { ...form, drive_url: link, jumlah: 1 });
      toast.success("Buku digital tersimpan");
      navigate("/pustaka/buku/digital");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Layout nav={NAV_PUSTAKA}>
      <PageTitle title="Input Buku Digital" action={
        <button onClick={() => navigate("/pustaka/buku/digital")} className="pp-btn-secondary">
          <ArrowLeft className="w-4 h-4" /> Kembali
        </button>
      } />
      <section className="pp-card p-6 sm:p-8 max-w-2xl">
        <form onSubmit={submit} className="space-y-4" data-testid="buku-digital-form">
          <div><label className="pp-label">Judul Buku *</label>
            <input required className="pp-input" value={form.judul} onChange={(e) => setForm({ ...form, judul: e.target.value })} data-testid="digital-judul" />
          </div>
          <div><label className="pp-label">Penulis *</label>
            <input required className="pp-input" value={form.penulis} onChange={(e) => setForm({ ...form, penulis: e.target.value })} data-testid="digital-penulis" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className="pp-label">Kategori</label>
              <select
                className="pp-input"
                value={form.kategori}
                onChange={(e) => setForm({ ...form, kategori: e.target.value, jenis_buku: e.target.value === "buku_teks_pelajaran" ? KELAS_TEKS[0] : JENIS_UMUM[0] })}
                data-testid="digital-kategori"
              >
                <option value="buku_teks_pelajaran">Buku Teks</option>
                <option value="buku_umum">Buku Umum</option>
              </select>
            </div>
            <div><label className="pp-label">{form.kategori === "buku_teks_pelajaran" ? "Kelas" : "Jenis"}</label>
              <select
                className="pp-input"
                value={form.jenis_buku}
                onChange={(e) => setForm({ ...form, jenis_buku: e.target.value })}
                data-testid="digital-jenis"
              >
                {jenisList.map(j => <option key={j}>{j}</option>)}
              </select>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="pp-label">Cover Buku</label>
              <label className="pp-btn-secondary cursor-pointer">
                <Upload className="w-4 h-4" />{uploadingCover ? "Mengunggah…" : (form.cover_file_id ? "Ganti Cover" : "Pilih Foto Cover")}
                <input type="file" accept="image/*" onChange={uploadCover} className="hidden" />
              </label>
              {form.cover_file_id && <div className="mt-2 text-xs text-emerald-600">✓ Cover siap</div>}
            </div>
            <div>
              <label className="pp-label">Link Google Drive PDF Buku *</label>
              <input
                type="url"
                required
                className="pp-input"
                placeholder="https://drive.google.com/file/d/..."
                value={form.drive_url}
                onChange={(e) => setForm({ ...form, drive_url: e.target.value })}
                data-testid="digital-drive-url"
              />
              <div className="mt-1 text-[11px] text-slate-500">Set akses file di Drive ke "Anyone with the link" agar bisa dibaca di portal.</div>
            </div>
          </div>
          <div className="flex justify-end pt-2 border-t border-slate-100">
            <button type="submit" disabled={saving} className="pp-btn-primary disabled:opacity-60" data-testid="submit-buku-digital">
              {saving ? "Menyimpan…" : "Simpan Buku Digital"}
            </button>
          </div>
        </form>
      </section>
    </Layout>
  );
}

/* ═══════════════════════ DAFTAR BUKU DIGITAL ═══════════════════════ */
export function DaftarBukuDigital() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [penulis, setPenulis] = useState("");
  const [kat, setKat] = useState("");
  const [jenis, setJenis] = useState("");
  const [applied, setApplied] = useState({ q: "", penulis: "", kat: "", jenis: "" });
  const navigate = useNavigate();

  const load = useCallback(async () => {
    const params = { format: "digital" };
    if (applied.q) params.q = applied.q;
    if (applied.penulis) params.penulis = applied.penulis;
    if (applied.kat) params.kategori = applied.kat;
    if (applied.jenis) params.jenis_buku = applied.jenis;
    const { data } = await api.get("/buku", { params });
    setItems(data);
  }, [applied]);
  useEffect(() => { load(); }, [load]);

  const apply = () => setApplied({ q, penulis, kat, jenis });
  const reset = () => { setQ(""); setPenulis(""); setKat(""); setJenis(""); setApplied({ q: "", penulis: "", kat: "", jenis: "" }); };

  const del = async (id) => { if (!window.confirm("Hapus buku digital?")) return; await api.delete(`/buku/${id}`); toast.success("Dihapus"); load(); };

  const jenisOptions = useMemo(() => {
    if (kat === "buku_teks_pelajaran") return KELAS_TEKS;
    if (kat === "buku_umum") return JENIS_UMUM;
    return [...KELAS_TEKS, ...JENIS_UMUM];
  }, [kat]);

  return (
    <Layout nav={NAV_PUSTAKA}>
      <PageTitle title="Daftar Buku Digital" action={
        <div className="flex flex-wrap gap-2">
          <ExcelBukuDigitalControls onReload={load} />
          <button onClick={() => navigate("/pustaka/buku/digital/input")} className="pp-btn-primary" data-testid="btn-input-buku-digital">
            <Plus className="w-4 h-4" /> Input Buku Digital
          </button>
        </div>
      } />

      <section className="pp-card p-5 mb-6 grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <input className="pp-input" placeholder="Cari judul" value={q} onChange={(e) => setQ(e.target.value)} data-testid="filter-judul-digital" />
        <input className="pp-input" placeholder="Penulis" value={penulis} onChange={(e) => setPenulis(e.target.value)} data-testid="filter-penulis-digital" />
        <select className="pp-input" value={kat} onChange={(e) => { setKat(e.target.value); setJenis(""); }} data-testid="filter-kategori-digital">
          <option value="">Semua kategori</option>
          <option value="buku_teks_pelajaran">Buku Teks</option>
          <option value="buku_umum">Buku Umum</option>
        </select>
        <select className="pp-input" value={jenis} onChange={(e) => setJenis(e.target.value)} data-testid="filter-jenis-digital">
          <option value="">Semua jenis / kelas</option>
          {jenisOptions.map(j => <option key={j}>{j}</option>)}
        </select>
        <div className="flex gap-2">
          <button onClick={apply} className="pp-btn-primary flex-1" data-testid="filter-apply-digital">Terapkan</button>
          <button onClick={reset} className="pp-btn-secondary flex-1" data-testid="filter-reset-digital">Reset</button>
        </div>
      </section>

      <section className="pp-card pp-freeze overflow-hidden">
        <table className="w-full text-sm" data-testid="buku-digital-table">
          <thead><tr className="pp-table-header">
            <th className="text-left px-5 py-3">Cover</th>
            <th className="text-left px-5 py-3">Judul</th>
            <th className="text-left px-5 py-3">Penulis</th>
            <th className="text-left px-5 py-3">Kategori</th>
            <th className="text-left px-5 py-3">Jenis / Kelas</th>
            <th className="text-right px-5 py-3">Aksi</th>
          </tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-slate-400 italic">Belum ada buku digital.</td></tr>}
            {items.map(b => (
              <tr key={b.id} className="pp-row">
                <td className="px-5 py-3">{b.cover_file_id ? <img src={fileUrl(b.cover_file_id)} className="w-10 h-14 object-cover rounded" alt="" /> : <div className="w-10 h-14 bg-slate-100 rounded flex items-center justify-center"><BookOpen className="w-5 h-5 text-slate-400" /></div>}</td>
                <td className="px-5 py-3 font-medium">{b.judul}</td>
                <td className="px-5 py-3 text-slate-600">{b.penulis}</td>
                <td className="px-5 py-3 text-xs">{b.kategori.replace("_", " ")}</td>
                <td className="px-5 py-3 text-xs">{b.jenis_buku}</td>
                <td className="px-5 py-3 text-right space-x-1">
                  <button onClick={() => navigate(`/pustaka/buku/digital/baca/${b.id}`)} className="pp-btn-primary" data-testid={`baca-${b.id}`}>
                    <BookOpen className="w-3.5 h-3.5" />Baca
                  </button>
                  <button onClick={() => del(b.id)} className="pp-btn-danger" data-testid={`del-digital-${b.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />Hapus
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </Layout>
  );
}

/* ═══════════════════════ BACA BUKU DIGITAL (Pustaka) ═══════════════════════ */
export function BacaBukuDigital() {
  return <BukuDigitalReader nav={NAV_PUSTAKA} backPath="/pustaka/buku/digital" />;
}

/* ═══════════════════════ PERMINTAAN / DAFTAR PEMINJAMAN (unchanged) ═══════════════════════ */
export function PermintaanPeminjaman() {
  const [items, setItems] = useState([]);
  const load = useCallback(async () => { const { data } = await api.get("/peminjaman", { params: { status: "pending" } }); setItems(data); }, []);
  useEffect(() => { load(); }, [load]);
  const approve = async (id) => { try { await api.post(`/peminjaman/${id}/approve`); toast.success("Disetujui"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const reject = async (id) => { try { await api.post(`/peminjaman/${id}/reject`); toast.success("Ditolak"); load(); } catch (e) { console.error(e); } };
  return (
    <Layout nav={NAV_PUSTAKA}>
      <PageTitle title="Permintaan Peminjaman" />
      <section className="pp-card pp-freeze overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="pp-table-header"><th className="text-left px-5 py-3">Judul Buku</th><th className="text-left px-5 py-3">Peminjam</th><th className="text-left px-5 py-3">Tgl Request</th><th className="text-center px-5 py-3">Lama (hari)</th><th className="text-right px-5 py-3">Aksi</th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-slate-400 italic">Tidak ada permintaan.</td></tr>}
            {items.map(p => (
              <tr key={p.id} className="pp-row">
                <td className="px-5 py-3 font-medium">{p.judul_buku}</td>
                <td className="px-5 py-3 text-slate-600">{p.peminjam_nama}</td>
                <td className="px-5 py-3 font-mono text-xs">{formatTanggal(p.tanggal_request)}</td>
                <td className="px-5 py-3 text-center">{p.lama_pinjam}</td>
                <td className="px-5 py-3 text-right space-x-1">
                  <button onClick={() => approve(p.id)} className="inline-flex items-center gap-1 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 rounded-lg px-3 py-1.5 text-xs"><Check className="w-3.5 h-3.5" />Setujui</button>
                  <button onClick={() => reject(p.id)} className="pp-btn-danger"><XCircle className="w-3.5 h-3.5" />Tolak</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </Layout>
  );
}

export function DaftarPeminjaman() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [appliedStatus, setAppliedStatus] = useState("");
  const load = useCallback(async () => { const params = {}; if (appliedStatus) params.status = appliedStatus; const { data } = await api.get("/peminjaman", { params }); setItems(data.filter(x => x.status !== "pending" && x.status !== "ditolak")); }, [appliedStatus]);
  useEffect(() => { load(); }, [load]);
  const ret = async (id) => { try { await api.post(`/peminjaman/${id}/return`); toast.success("Buku dikembalikan"); load(); } catch (e) { console.error(e); } };
  return (
    <Layout nav={NAV_PUSTAKA}>
      <PageTitle title="Daftar Peminjaman" />
      <section className="pp-card p-5 mb-6 flex items-end gap-2 flex-wrap">
        <div>
          <label className="pp-label">Filter status</label>
          <select className="pp-input max-w-xs" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="peminjaman-filter-status">
            <option value="">Semua</option><option value="dipinjam">Dipinjam</option><option value="dikembalikan">Dikembalikan</option>
          </select>
        </div>
        <button onClick={() => setAppliedStatus(status)} className="pp-btn-primary" data-testid="peminjaman-filter-apply">Terapkan</button>
        <button onClick={() => { setStatus(""); setAppliedStatus(""); }} className="pp-btn-secondary" data-testid="peminjaman-filter-reset">Reset</button>
      </section>
      <section className="pp-card pp-freeze overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="pp-table-header"><th className="text-left px-5 py-3">Judul</th><th className="text-left px-5 py-3">Peminjam</th><th className="text-left px-5 py-3">Tgl Pinjam</th><th className="text-left px-5 py-3">Tgl Kembali</th><th className="text-left px-5 py-3">Status</th><th className="text-right px-5 py-3">Aksi</th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-slate-400 italic">Belum ada peminjaman.</td></tr>}
            {items.map(p => (
              <tr key={p.id} className="pp-row">
                <td className="px-5 py-3 font-medium">{p.judul_buku}</td>
                <td className="px-5 py-3 text-slate-600">{p.peminjam_nama}</td>
                <td className="px-5 py-3 font-mono text-xs">{p.tanggal_pinjam || "—"}</td>
                <td className="px-5 py-3 font-mono text-xs">{p.tanggal_kembali || "—"}</td>
                <td className="px-5 py-3"><span className={p.status === "dipinjam" ? "pp-badge-info" : "pp-badge-success"}>{p.status}</span></td>
                <td className="px-5 py-3 text-right">{p.status === "dipinjam" && <button onClick={() => ret(p.id)} className="pp-btn-secondary text-xs"><Undo2 className="w-3.5 h-3.5" />Tandai Kembali</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </Layout>
  );
}
